import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Alert, Badge } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import useProductApi, { Product } from '../../managers/ProductManager';
import useImageApi from '../../managers/ImageManager';
import TemplateForm from './component/TemplateForm';
import Loading, { LoadingStates } from '../../components/Loading';
import { Helmet } from 'react-helmet-async';

// Creative loading messages for AI generation
const GENERATION_MESSAGES = [
  "Bringing your vision to life...",
  "AI is painting your masterpiece...",
  "Take a deep breath, magic is happening...",
  "Close your eyes and dream while we create...",
  "Transforming pixels into perfection...",
  "Your artwork is emerging from digital dreams...",
  "Weaving creativity with AI...",
  "Crafting something beautiful just for you...",
  "Mixing imagination with innovation...",
  "Watch your ideas bloom into reality..."
];

const Template: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated, loginWithRedirect } = useAuth0();
  const { getProduct, generateFromTemplate, generateProduct } = useProductApi();
  const { generateImageAsync } = useImageApi();

  const [template, setTemplate] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    const fetchTemplate = async () => {
      if (!productId) {
        setError('Invalid template ID');
        setLoading(false);
        return;
      }

      try {
        const product = await getProduct(productId);

        if (product.statusTag !== 'TEMPLATE') {
          setError('This product is not a template');
          setLoading(false);
          return;
        }

        setTemplate(product);
      } catch (err: any) {
        console.error('Error fetching template:', err);
        setError(err.message || 'Failed to load template');
      } finally {
        setLoading(false);
      }
    };

    fetchTemplate();
  }, [productId]);

  // Cycle through generation messages every 15 seconds while generating
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (generating) {
      interval = setInterval(() => {
        setMessageIndex((prevIndex) => {
          const newIndex = (prevIndex + 1) % GENERATION_MESSAGES.length;
          setLoadingMessage(GENERATION_MESSAGES[newIndex]);
          return newIndex;
        });
      }, 15000); // 15 seconds
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [generating]);

  const handleSubmit = async (parameters: Record<string, any>) => {
    if (!user) {
      setError('User not found');
      return;
    }

    if (!productId) {
      setError('Invalid template ID');
      return;
    }

    // Start with a random creative message for AI generation
    const randomIndex = Math.floor(Math.random() * GENERATION_MESSAGES.length);
    setMessageIndex(randomIndex);
    setLoadingMessage(GENERATION_MESSAGES[randomIndex]);
    setGenerating(true);
    setError(null);

    try {
      // Step 1: Get template parameters (NO generation - backend just returns parameters)
      console.log('Step 1: Getting template parameters with user input:', parameters);
      const templateParams = await generateFromTemplate(productId, parameters);
      console.log('Template parameters received:', templateParams);

      setLoadingMessage('AI is creating your masterpiece...');

      // Step 2: Generate image using same method as Create.tsx
      console.log('Step 2: Generating image with generateImageAsync (same as Create.tsx)');
      const image = await generateImageAsync({
        userId: user.sub || "0",
        action: templateParams.base64Image ? 'generate-from-upload' : 'generate',
        prompt: templateParams.prompt,
        imageShape: templateParams.imageShape,
        imageStyle: templateParams.imageStyle,
        imageModel: templateParams.imageModel,
        imageName: templateParams.imageName,
        imageTags: templateParams.imageTags,
        base64Image: templateParams.base64Image,
        vendorId: 'openai',
        catalogItemId: templateParams.catalogItemId,
        catalogItemVariantId: templateParams.catalogItemVariantId
      });

      console.log('Image generation completed:', image);

      setLoadingMessage('Creating product preview...');

      // Step 3: Create product (same as Create.tsx)
      console.log('Step 3: Creating product with generated image');
      const product = await generateProduct({
        userId: user.sub || "0",
        sourceImageUrl: image.imageUrl,
        sourceImageId: image.imageId,
        catalogItemId: templateParams.catalogItemId,
        catalogItemVariantId: templateParams.catalogItemVariantId
      });

      console.log('Product created successfully:', product);

      // Step 4: Navigate (same as Create.tsx)
      const category = templateParams.catalogItemCategory || template?.catalogItemCategory || 'uncategorized';
      const productUrl = `/products/${templateParams.catalogItemId}/${category}/${product.productId}`;
      console.log('Navigating to product page:', productUrl);
      navigate(productUrl);

    } catch (err: any) {
      console.error('Error generating from template:', err);
      setError(err.message || 'Failed to generate product from template');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return <Loading state={LoadingStates.LOADING} message="Loading template..." />;
  }

  if (error && !template) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  if (!template || !template.templateConfig) {
    return (
      <Container className="py-5">
        <Alert variant="warning">Template configuration not found</Alert>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`${template.name || 'Template'} | Wallscapes.AI`}</title>
        <meta name="description" content={`Customize this ${template.name || 'template'} design. Create your own unique AI-generated art for print-on-demand products.`} />
      </Helmet>
      {/* Overlay Loading Screen */}
      {generating && (
        <Loading
          state={LoadingStates.LOADING}
          message={loadingMessage}
          subtitle="Please don't close this window. We're creating something amazing for you!"
          overlay={true}
          image={template.previewImageUrls?.[0]}
        />
      )}

      <Container className="py-4">
        <Row className="justify-content-center">
          <Col xs={12} md={7} className="text-center">
            <img
              src={template.previewImageUrls?.[0]}
              alt="Generated Product"
              className="img-fluid"
              style={{ maxWidth: '100%', objectFit: 'contain', borderRadius: "8px" }}
            />
          </Col>

          {/* Template Form */}
          <Col xs={12} md={5}>
            <div className="d-flex align-items-center gap-2 mb-2">
              <h3>{template.name}</h3>
              <Badge bg="primary">TEMPLATE</Badge>
            </div>
            {template.description && (
              <p className="text-muted">{template.description}</p>
            )}
            <p>This is an example of what your customized product might look like.
              Fill in the form to create your own unique version.</p>

            {error && (
              <Alert variant="danger" dismissible onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <TemplateForm
              parameters={template.templateConfig.parameters}
              onSubmit={handleSubmit}
              loading={generating}
              isAuthenticated={isAuthenticated}
              onLoginClick={() => loginWithRedirect({
                appState: { returnTo: window.location.pathname }
              })}
            />
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Template;
